import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { apiService } from '@/services/api';

interface EmployeePayslipViewerProps {
	employeeId: string;
	employeeName: string;
}

interface SalaryRecord {
	id: number;
	basic_pay: number;
	salary_scale_id?: number | null;
	start_date: string;
	end_date?: string | null;
}

interface PayrollItemAssignment {
	id: number;
	payroll_item_id: number;
	name: string;
	type: 'allowance' | 'deduction';
	method: 'fixed' | 'percentage';
	value: number; // fixed amount or percent
	percentage_base?: 'basic' | 'gross' | 'net' | null;
	is_mandatory?: number;
	start_date: string;
	end_date?: string | null;
}

interface MonthlyItem {
	id: number;
	name: string;
	type: 'fixed' | 'percentage';
	value: number;
	percentage_base?: 'basic' | 'gross' | 'net' | '';
	month: string; // YYYY-MM-01
}

function isActiveInMonth(start: string, end: string | null | undefined, year: number, month: number): boolean {
	const firstOfMonth = new Date(year, month - 1, 1);
	const lastOfMonth = new Date(year, month, 0);
	const startDate = new Date(start);
	const endDate = end ? new Date(end) : null;
	return startDate <= lastOfMonth && (!endDate || endDate >= firstOfMonth);
}

function pickSalaryForMonth(records: SalaryRecord[], year: number, month: number): SalaryRecord | null {
	if (!records || records.length === 0) return null;
	const match = records.find(r => isActiveInMonth(r.start_date, r.end_date, year, month));
	if (match) return match;
	// fallback to most recent
	return records.sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime())[0] || null;
}

export const EmployeePayslipViewer = ({ employeeId, employeeName }: EmployeePayslipViewerProps) => {
	const [yearMonth, setYearMonth] = useState<string>(() => new Date().toISOString().slice(0, 7)); // YYYY-MM
	const [loading, setLoading] = useState(false);
	const [salaryHistory, setSalaryHistory] = useState<SalaryRecord[]>([]);
	const [activeAssignments, setActiveAssignments] = useState<PayrollItemAssignment[]>([]);
	const [monthlyAllowances, setMonthlyAllowances] = useState<MonthlyItem[]>([]);
	const [monthlyDeductions, setMonthlyDeductions] = useState<MonthlyItem[]>([]);

	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			setError(null);
			try {
				const employeeIdNum = parseInt(employeeId, 10);
				const [salariesRes, assignmentsRes, allowancesRes, deductionsRes] = await Promise.all([
					apiService.getEmployeeSalaries(String(employeeIdNum)),
					apiService.getActiveEmployeePayrollItems(String(employeeIdNum)),
					apiService.getEmployeeAllowances(employeeIdNum),
					apiService.getEmployeeDeductions(employeeIdNum)
				]);

				setSalaryHistory(Array.isArray(salariesRes.data) ? salariesRes.data : []);
				setActiveAssignments(Array.isArray(assignmentsRes.data) ? assignmentsRes.data : []);
				setMonthlyAllowances(Array.isArray(allowancesRes.data) ? allowancesRes.data : []);
				setMonthlyDeductions(Array.isArray(deductionsRes.data) ? deductionsRes.data : []);
			} catch (e: any) {
				setError('Failed to load payslip data');
				console.error(e);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [employeeId]);

	const selectedYear = useMemo(() => parseInt(yearMonth.split('-')[0], 10), [yearMonth]);
	const selectedMonth = useMemo(() => parseInt(yearMonth.split('-')[1], 10), [yearMonth]);

	const monthAllowanceItems = useMemo(() => {
		const monthKey = `${yearMonth}-01`;
		return monthlyAllowances.filter(a => a.month.slice(0, 7) === yearMonth);
	}, [monthlyAllowances, yearMonth]);

	const monthDeductionItems = useMemo(() => {
		const monthKey = `${yearMonth}-01`;
		return monthlyDeductions.filter(d => d.month.slice(0, 7) === yearMonth);
	}, [monthlyDeductions, yearMonth]);

	const assignmentItemsForMonth = useMemo(() => {
		return activeAssignments.filter(a => isActiveInMonth(a.start_date, a.end_date, selectedYear, selectedMonth));
	}, [activeAssignments, selectedYear, selectedMonth]);

	const salaryForMonth = useMemo(() => pickSalaryForMonth(salaryHistory, selectedYear, selectedMonth), [salaryHistory, selectedYear, selectedMonth]);

	const computed = useMemo(() => {
		const basic = Number((salaryForMonth?.basic_pay as unknown) ?? 0) || 0;

		// Helper to compute amount for a percentage item
		const computePercent = (percent: number, base: 'basic' | 'gross' | 'net', grossSoFar: number, netSoFar: number) => {
			switch (base) {
				case 'basic':
					return (percent / 100) * basic;
				case 'gross':
					return (percent / 100) * Math.max(grossSoFar, basic);
				case 'net':
					return (percent / 100) * Math.max(netSoFar, basic);
				default:
					return 0;
			}
		};

		// Start with fixed components
		let allowanceFixed = 0;
		let deductionFixed = 0;

		const allAllowances = [
			...assignmentItemsForMonth.filter(i => i.type === 'allowance').map(i => ({
				label: i.name,
				method: i.method,
				value: Number((i.value as unknown) ?? 0) || 0,
				percentage_base: (i.percentage_base || 'basic') as 'basic' | 'gross' | 'net',
			})),
			...monthAllowanceItems.map(i => ({
				label: i.name,
				method: i.type as 'fixed' | 'percentage',
				value: Number((i.value as unknown) ?? 0) || 0,
				percentage_base: (i.percentage_base || 'basic') as 'basic' | 'gross' | 'net',
			}))
		];

		const allDeductions = [
			...assignmentItemsForMonth.filter(i => i.type === 'deduction').map(i => ({
				label: i.name,
				method: i.method,
				value: Number((i.value as unknown) ?? 0) || 0,
				percentage_base: (i.percentage_base || 'basic') as 'basic' | 'gross' | 'net',
			})),
			...monthDeductionItems.map(i => ({
				label: i.name,
				method: i.type as 'fixed' | 'percentage',
				value: Number((i.value as unknown) ?? 0) || 0,
				percentage_base: (i.percentage_base || 'basic') as 'basic' | 'gross' | 'net',
			}))
		];

		// Apply fixed first
		for (const a of allAllowances) if (a.method === 'fixed') allowanceFixed += a.value;
		for (const d of allDeductions) if (d.method === 'fixed') deductionFixed += d.value;

		let gross = basic + allowanceFixed; // exclude percentage allowances initially
		let net = gross - deductionFixed; // before percentage-based items

		// Then apply percentage-based allowances (based on base rule)
		let allowancePercent = 0;
		for (const a of allAllowances) {
			if (a.method === 'percentage') {
				allowancePercent += computePercent(a.value, a.percentage_base, gross, net);
				gross = basic + allowanceFixed + allowancePercent;
				net = gross - deductionFixed; // deductions applied later
			}
		}

		// Apply percentage-based deductions last
		let deductionPercent = 0;
		for (const d of allDeductions) {
			if (d.method === 'percentage') {
				deductionPercent += computePercent(d.value, d.percentage_base, gross, net);
				net = gross - (deductionFixed + deductionPercent);
			}
		}

		return {
			basic,
			allowanceFixed,
			allowancePercent,
			gross,
			deductionFixed,
			deductionPercent,
			net,
			allowanceBreakdown: allAllowances,
			deductionBreakdown: allDeductions,
		};
	}, [salaryForMonth, assignmentItemsForMonth, monthAllowanceItems, monthDeductionItems]);

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
				<div className="md:col-span-2">
					<Card>
						<CardHeader>
							<CardTitle>{employeeName}</CardTitle>
						</CardHeader>
						{/* <CardContent className="text-sm text-gray-600">
							Employee ID: {employeeId}
						</CardContent> */}
					</Card>
				</div>
				<div>
					<Label htmlFor="payslip_month">Month</Label>
					<Input id="payslip_month" type="month" value={yearMonth} onChange={(e) => setYearMonth(e.target.value)} disabled />
				</div>
			</div>

			{loading ? (
				<div className="flex items-center justify-center h-32">Loading...</div>
			) : error ? (
				<div className="text-red-600">{error}</div>
			) : (
				<Card>
					<CardHeader>
						<CardTitle>Payslip Preview - {new Date(`${yearMonth}-01`).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<h3 className="font-semibold mb-2">Earnings</h3>
								<div className="flex justify-between py-1"><span>Basic Pay</span><span>ZMW {computed.basic.toFixed(2)}</span></div>
								{computed.allowanceBreakdown.map((a, idx) => (
									<div key={`a-${idx}`} className="flex justify-between py-1 text-sm text-gray-700">
										<span>
											<Badge variant="outline" className="mr-2">{a.method === 'fixed' ? 'Fixed' : 'Percent'}</Badge>
											{a.label}
										</span>
										<span>
											{a.method === 'fixed' ? `ZMW ${Number(a.value).toFixed(2)}` : `${a.value}% of ${a.percentage_base}`}
										</span>
									</div>
								))}
								<Separator className="my-2" />
								<div className="flex justify-between font-medium"><span>Gross</span><span>ZMW {computed.gross.toFixed(2)}</span></div>
							</div>

							<div>
								<h3 className="font-semibold mb-2">Deductions</h3>
								{computed.deductionBreakdown.map((d, idx) => (
									<div key={`d-${idx}`} className="flex justify-between py-1 text-sm text-gray-700">
										<span>
											<Badge variant="outline" className="mr-2">{d.method === 'fixed' ? 'Fixed' : 'Percent'}</Badge>
											{d.label}
										</span>
										<span>
											{d.method === 'fixed' ? `ZMW ${Number(d.value).toFixed(2)}` : `${d.value}% of ${d.percentage_base}`}
										</span>
									</div>
								))}
								<Separator className="my-2" />
								<div className="flex justify-between font-medium"><span>Total Deductions</span><span>ZMW {(computed.deductionFixed + computed.deductionPercent).toFixed(2)}</span></div>
								<Separator className="my-2" />
								<div className="flex justify-between text-lg font-semibold"><span>Net Pay</span><span>ZMW {computed.net.toFixed(2)}</span></div>
							</div>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
};
